/**
 * codeFormat · GLOBAL code-card / terminal formatter (2026-06-25).
 *
 * Applies automatically to EVERY `<pre><code>` in rendered ticket content — no
 * per-sprint markup edits. Two surfaces:
 *   - code   → `.codecard` (bar with language label + copy · syntax-highlighted body)
 *   - bash/terminal → `.term` (bar `>_` + «терминал» + copy · prompt/comment dimming)
 *
 * EXCLUSIONS:
 *   - any `<code>` inside `.code-along` is SKIPPED (codeAlong.ts owns those — they
 *     carry interactive `.ca-blank` spans that must NOT be tokenized).
 *   - already-wrapped blocks are guarded via `code.dataset.cf === '1'`.
 *
 * Language detection (in priority order):
 *   1. explicit class `language-xxx` or `data-lang` on the <code> or its <pre>.
 *   2. bash/terminal heuristic — every non-empty line starts with `$ ` OR with a
 *      known shell command (pip/npm/npx/playwright/python /cd /git /curl /pytest /node ).
 *   3. sql heuristic — text contains a SQL keyword (SELECT/INSERT/UPDATE/DELETE/FROM/WHERE).
 *   4. js heuristic — text contains const / => / function.
 *   5. python by default.
 *
 * HIGHLIGHTING is applied to code only (never the terminal). The tokenizer is a
 * defensive single-pass scanner: it escapes HTML first, matches comments and
 * strings FIRST (so keywords/numbers inside them are not re-coloured), then
 * numbers, keywords, and the function name (identifier immediately before `(`).
 * On any unexpected input it degrades to escaped plain text — it never throws.
 *
 * Copy: Clipboard API first, hidden-textarea execCommand fallback (mirrors
 * copyBlocks.ts · jsdom-safe). Terminal copy strips `$ ` prompts. Feedback =
 * «Скопировано» + `.done` for 1.5s.
 *
 * StrictMode-safe: guard `code.dataset.cf==='1'` so React 18 dev double-invoke
 * does not produce duplicate cards; cleanup removes listeners + clears timers.
 * (См. feedback_qa_studio_strictmode_interactive_modules.)
 */

type Lang = 'python' | 'sql' | 'bash' | 'js';

const LANG_LABEL: Record<Lang, string> = {
  python: 'PYTHON',
  sql: 'SQL',
  bash: 'BASH',
  js: 'JS',
};

/** Shell-command prefixes that mark a line as terminal even without a `$`. */
const SHELL_PREFIX =
  /^(pip|npm|npx|playwright|python |cd |git |curl |pytest |node )/;

const SQL_KW = [
  'SELECT', 'INSERT', 'UPDATE', 'DELETE', 'FROM', 'WHERE', 'JOIN', 'LEFT',
  'RIGHT', 'INNER', 'ON', 'GROUP', 'BY', 'ORDER', 'LIMIT', 'HAVING', 'AND',
  'OR', 'NOT', 'NULL', 'AS', 'DESC', 'ASC', 'COUNT', 'SUM', 'AVG',
];
const PY_KW = [
  'from', 'import', 'as', 'with', 'def', 'return', 'if', 'else', 'elif',
  'for', 'while', 'in', 'not', 'and', 'or', 'None', 'True', 'False', 'class',
  'lambda', 'try', 'except',
];
const JS_KW = [
  'const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while',
  'await', 'async', 'import', 'from', 'export', 'new', 'class', 'true', 'false',
  'null', 'undefined', 'typeof', 'this',
];
const BASH_KW = [
  'pip', 'npm', 'npx', 'playwright', 'python', 'pytest', 'node', 'cd', 'git',
  'curl', 'install', 'run', 'test', 'export',
];

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Lines, ignoring blank ones, for the terminal heuristic. */
function nonEmptyLines(text: string): string[] {
  return text.split('\n').map((l) => l.trim()).filter(Boolean);
}

function looksTerminal(text: string): boolean {
  const lines = nonEmptyLines(text);
  if (!lines.length) return false;
  return lines.every((l) => l.startsWith('$ ') || SHELL_PREFIX.test(l));
}

function detectLang(code: HTMLElement, pre: HTMLElement | null, text: string): Lang | null {
  // 1 · explicit class / data-lang on code or pre.
  const explicit = (el: HTMLElement | null): string | null => {
    if (!el) return null;
    if (el.dataset.lang) return el.dataset.lang.toLowerCase();
    const m = /(?:^|\s)language-([a-z0-9+#]+)/i.exec(el.className);
    return m?.[1] ? m[1].toLowerCase() : null;
  };
  const tag = explicit(code) ?? explicit(pre);
  if (tag) {
    if (tag === 'bash' || tag === 'sh' || tag === 'shell' || tag === 'terminal') return 'bash';
    if (tag === 'sql') return 'sql';
    if (tag === 'js' || tag === 'javascript' || tag === 'ts' || tag === 'typescript') return 'js';
    if (tag === 'py' || tag === 'python') return 'python';
    // Unknown explicit tag → fall through to heuristics.
  }

  // 2 · terminal heuristic.
  if (looksTerminal(text)) return 'bash';

  // 3 · sql heuristic.
  if (/\b(SELECT|INSERT|UPDATE|DELETE|FROM|WHERE)\b/i.test(text)) return 'sql';

  // 4 · js heuristic.
  if (/\bconst\b|=>|\bfunction\b/.test(text)) return 'js';

  // 5 · python default.
  return 'python';
}

/**
 * Tokenize one line of CODE (not terminal) → safe HTML with .tok-* spans.
 * Defensive single-pass scanner. Comments + strings are consumed whole so their
 * interior is never re-highlighted. Returns escaped plain text if anything looks
 * off (the catch-all guarantees we never inject broken markup).
 */
function highlightLine(line: string, lang: Lang): string {
  try {
    const kws =
      lang === 'sql' ? SQL_KW : lang === 'js' ? JS_KW : lang === 'bash' ? BASH_KW : PY_KW;
    // SQL keywords are matched case-insensitively; others case-sensitively.
    const kwSet = new Set(kws);
    const kwLower = new Set(kws.map((k) => k.toLowerCase()));
    const commentToken = lang === 'sql' ? '--' : lang === 'js' ? '//' : '#';

    let out = '';
    let i = 0;
    const n = line.length;

    const isIdentStart = (c: string) => /[A-Za-z_]/.test(c);
    const isIdent = (c: string) => /[A-Za-z0-9_]/.test(c);

    while (i < n) {
      const c = line[i]!;

      // Comment → rest of line.
      if (line.startsWith(commentToken, i)) {
        out += `<span class="tok-c">${escapeHtml(line.slice(i))}</span>`;
        i = n;
        break;
      }

      // String literal "…" or '…' (no escape handling needed for display).
      if (c === '"' || c === "'") {
        const quote = c;
        let j = i + 1;
        while (j < n && line[j] !== quote) {
          if (line[j] === '\\' && j + 1 < n) j += 2;
          else j += 1;
        }
        // include closing quote if present
        const end = j < n ? j + 1 : n;
        out += `<span class="tok-s">${escapeHtml(line.slice(i, end))}</span>`;
        i = end;
        continue;
      }

      // Number.
      if (/[0-9]/.test(c) && (i === 0 || !isIdent(line[i - 1]!))) {
        let j = i + 1;
        while (j < n && /[0-9.]/.test(line[j]!)) j += 1;
        const num = line.slice(i, j);
        if (/^\d+(\.\d+)?$/.test(num)) {
          out += `<span class="tok-n">${num}</span>`;
          i = j;
          continue;
        }
      }

      // Identifier → keyword or function name or plain.
      if (isIdentStart(c)) {
        let j = i + 1;
        while (j < n && isIdent(line[j]!)) j += 1;
        const word = line.slice(i, j);
        // Look ahead past spaces for a '(' to detect a call.
        let k = j;
        while (k < n && line[k] === ' ') k += 1;
        const isCall = line[k] === '(';
        const isKw = lang === 'sql' ? kwLower.has(word.toLowerCase()) : kwSet.has(word);
        if (isKw) {
          out += `<span class="tok-k">${escapeHtml(word)}</span>`;
        } else if (isCall) {
          out += `<span class="tok-f">${escapeHtml(word)}</span>`;
        } else {
          out += escapeHtml(word);
        }
        i = j;
        continue;
      }

      // Anything else — single char, escaped.
      out += escapeHtml(c);
      i += 1;
    }
    return out;
  } catch {
    return escapeHtml(line);
  }
}

export function highlight(text: string, lang: Lang = 'python'): string {
  return text.split('\n').map((line) => highlightLine(line, lang)).join('\n');
}

/**
 * ── Safe, dependency-free beautifier (homework CodeForm · Genie «2а») ──
 * Language-aware and CONSERVATIVE: never rewrites semantics, only whitespace/case.
 * - js   → re-indent by bracket depth (2-space), strings/comments guarded.
 * - sql  → uppercase known keywords (outside strings) + newline before major clauses.
 * - python → only rtrim + tabs→4 spaces + collapse blank runs (indentation is SYNTAX — untouched).
 * - bash → rtrim + collapse blank runs.
 * On ANY unexpected input it returns the original text (try/catch) — a format button must never corrupt code.
 */
export type BeautifyLang = Lang;

function rtrimCollapse(text: string): string {
  return text
    .split('\n')
    .map((l) => l.replace(/[ \t]+$/, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+/, '')
    .replace(/\n+$/, '');
}

/** Count bracket open/close on a code line, ignoring brackets inside strings/// comments. */
function bracketScan(s: string): { open: number; close: number; leadClose: number } {
  let open = 0, close = 0, leadClose = 0, i = 0, seen = false;
  const n = s.length;
  while (i < n) {
    const c = s[i]!;
    if (c === '"' || c === "'" || c === '`') {
      const q = c; i += 1;
      while (i < n && s[i] !== q) { if (s[i] === '\\') i += 1; i += 1; }
      i += 1; seen = true; continue;
    }
    if (c === '/' && s[i + 1] === '/') break;
    if (c === '{' || c === '(' || c === '[') { open += 1; seen = true; }
    else if (c === '}' || c === ')' || c === ']') { close += 1; if (!seen) leadClose += 1; }
    else if (c !== ' ' && c !== '\t') seen = true;
    i += 1;
  }
  return { open, close, leadClose };
}

function beautifyBraces(text: string): string {
  const lines = text.replace(/\t/g, '  ').split('\n');
  const out: string[] = [];
  let depth = 0;
  for (const raw of lines) {
    const s = raw.trim();
    if (s === '') { out.push(''); continue; }
    const { open, close, leadClose } = bracketScan(s);
    const indent = Math.max(0, depth - leadClose);
    out.push('  '.repeat(indent) + s);
    depth = Math.max(0, depth + open - close);
  }
  return rtrimCollapse(out.join('\n'));
}

const SQL_BEAUTIFY_KW = [
  'select', 'from', 'where', 'group by', 'order by', 'having', 'limit', 'offset',
  'inner join', 'left join', 'right join', 'full join', 'join', 'on', 'and', 'or',
  'as', 'in', 'not', 'null', 'is', 'like', 'between', 'union', 'all', 'distinct',
  'count', 'sum', 'avg', 'min', 'max', 'insert', 'into', 'values', 'update', 'set',
  'delete', 'desc', 'asc', 'case', 'when', 'then', 'else', 'end',
];
const SQL_CLAUSE_BREAK = /\s+(FROM|WHERE|GROUP BY|ORDER BY|HAVING|LIMIT|OFFSET|INNER JOIN|LEFT JOIN|RIGHT JOIN|FULL JOIN|JOIN|UNION)\b/g;

/** Apply a transform only to the non-string segments of `sql`. */
function mapOutsideStrings(sql: string, fn: (seg: string) => string): string {
  let out = '', i = 0; const n = sql.length;
  let seg = '';
  while (i < n) {
    const c = sql[i]!;
    if (c === '"' || c === "'" || c === '`') {
      out += fn(seg); seg = '';
      const q = c; let j = i + 1;
      while (j < n && sql[j] !== q) { if (sql[j] === '\\') j += 1; j += 1; }
      const end = j < n ? j + 1 : n;
      out += sql.slice(i, end);
      i = end; continue;
    }
    seg += c; i += 1;
  }
  out += fn(seg);
  return out;
}

function beautifySql(text: string): string {
  const kwAlt = SQL_BEAUTIFY_KW
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((k) => k.replace(/ /g, '\\s+'))
    .join('|');
  const kwRe = new RegExp(`\\b(${kwAlt})\\b`, 'gi');
  let s = mapOutsideStrings(text, (seg) =>
    seg
      .replace(/\s+/g, ' ')
      .replace(kwRe, (m) => m.toUpperCase().replace(/\s+/g, ' ')),
  );
  s = mapOutsideStrings(s, (seg) => seg.replace(SQL_CLAUSE_BREAK, '\n$1'));
  // indent ON/AND/OR continuations one level under their clause
  s = s.split('\n').map((l) => (/^(AND|OR|ON)\b/.test(l.trim()) ? '  ' + l.trim() : l.trim())).join('\n');
  return rtrimCollapse(s);
}

export function beautify(text: string, lang: BeautifyLang = 'js'): string {
  try {
    if (lang === 'js') return beautifyBraces(text);
    if (lang === 'sql') return beautifySql(text);
    if (lang === 'python') return rtrimCollapse(text.replace(/\t/g, '    '));
    return rtrimCollapse(text); // bash + fallback
  } catch {
    return text;
  }
}

/** Chars that signal an inline `<code>` chip is actually code (not a plain label). */
const CODE_SIGNAL = '()[]{}=+-*/<>."\'#:;,|&%!?';
/**
 * Should an inline `<code>` chip be syntax-highlighted? True if it carries a code
 * signal char, OR is a single language keyword/literal (True/False/None/if/for…),
 * OR is a pure number. Plain-word labels (e.g. «Log in», «Drag and drop») return
 * false so we never miscolour the keyword «in»/«for» hiding inside prose.
 */
function looksLikeCode(text: string, lang: Lang): boolean {
  const s = text.trim();
  if (!s) return false;
  for (const ch of s) if (CODE_SIGNAL.includes(ch)) return true;
  const kws = lang === 'sql' ? SQL_KW : lang === 'js' ? JS_KW : lang === 'bash' ? BASH_KW : PY_KW;
  if (lang === 'sql' ? kws.some((k) => k.toLowerCase() === s.toLowerCase()) : kws.includes(s)) {
    return true;
  }
  return /^\d+(\.\d+)?$/.test(s);
}

/** Terminal body markup: dim `$ ` prompt + trailing `# comment`. */
function terminalBody(text: string): string {
  return text
    .split('\n')
    .map((raw) => {
      const line = raw;
      // Trailing comment (`# …`) — split once, keep prefix highlighted plain.
      const hashIdx = line.indexOf('#');
      let prefix = line;
      let comment = '';
      if (hashIdx >= 0) {
        prefix = line.slice(0, hashIdx);
        comment = line.slice(hashIdx);
      }
      let head = '';
      let body = prefix;
      const m = /^(\s*)\$ /.exec(prefix);
      if (m) {
        head = `<span class="pr">$</span>`;
        body = prefix.slice(m[0].length);
      }
      const commentHtml = comment ? `<span class="com">${escapeHtml(comment)}</span>` : '';
      return `${escapeHtml(m?.[1] ?? '')}${head}${escapeHtml(body)}${commentHtml}`;
    })
    .join('\n');
}

/** Strip leading `$ ` prompts so copied terminal text is paste-ready. */
function terminalCopyText(text: string): string {
  return text
    .split('\n')
    .map((line) => line.replace(/^(\s*)\$ /, '$1'))
    .join('\n');
}

function copyToClipboard(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      resolve();
    } catch (err) {
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  });
}

/** Build a copy button + wire feedback. Returns the button + a cleanup. */
function makeCopyButton(getText: () => string): { btn: HTMLButtonElement; cleanup: () => void } {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'cc-copy';
  btn.textContent = 'Копировать';
  let timer: ReturnType<typeof setTimeout> | undefined;
  const onClick = () => {
    const done = () => {
      btn.textContent = 'Скопировано';
      btn.classList.add('done');
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        btn.textContent = 'Копировать';
        btn.classList.remove('done');
      }, 1500);
    };
    // Feedback either way — sandboxed previews may deny clipboard.
    copyToClipboard(getText()).then(done, done);
  };
  btn.addEventListener('click', onClick);
  return {
    btn,
    cleanup: () => {
      btn.removeEventListener('click', onClick);
      if (timer) clearTimeout(timer);
    },
  };
}

export function initCodeFormat(root: HTMLElement): () => void {
  if (typeof document === 'undefined') return () => {};
  const cleanups: Array<() => void> = [];

  const codes = Array.from(root.querySelectorAll<HTMLElement>('pre > code'));
  for (const code of codes) {
    // Skip interactive blocks owned by other engines (code-along · py-run editor).
    if (code.closest('.code-along') || code.closest('.py-run') || code.closest('.po-build') || code.closest('.rflow') || code.closest('.pforge')) continue;
    // StrictMode / re-init guard.
    if (code.dataset.cf === '1') continue;

    const pre = code.parentElement as HTMLElement | null;
    if (!pre || pre.tagName !== 'PRE') continue;

    const rawText = code.textContent ?? '';
    if (!rawText.trim()) {
      code.dataset.cf = '1';
      continue;
    }

    const lang = detectLang(code, pre, rawText);
    if (!lang) {
      code.dataset.cf = '1';
      continue;
    }

    code.dataset.cf = '1';

    if (lang === 'bash') {
      // Terminal surface.
      const term = document.createElement('div');
      term.className = 'term';

      const bar = document.createElement('div');
      bar.className = 'term-bar';
      const mark = document.createElement('span');
      mark.className = 'term-mark';
      mark.textContent = '>_';
      const ttl = document.createElement('span');
      ttl.className = 'term-ttl';
      ttl.textContent = 'терминал';
      const { btn, cleanup } = makeCopyButton(() => terminalCopyText(rawText));
      cleanups.push(cleanup);
      bar.append(mark, ttl, btn);

      const body = document.createElement('pre');
      body.className = 'term-body';
      body.innerHTML = terminalBody(rawText);

      term.append(bar, body);
      pre.replaceWith(term);
      cleanups.push(() => {
        // Restore the original <pre> on cleanup so a fresh init re-enhances.
        term.replaceWith(pre);
        delete code.dataset.cf;
      });
    } else {
      // Code card surface.
      const card = document.createElement('div');
      card.className = 'codecard';

      const bar = document.createElement('div');
      bar.className = 'cc-bar';
      const label = document.createElement('span');
      label.className = 'cc-lang';
      label.textContent = LANG_LABEL[lang];
      const { btn, cleanup } = makeCopyButton(() => rawText);
      cleanups.push(cleanup);
      bar.append(label, btn);

      const body = document.createElement('pre');
      body.className = 'cc-body';
      body.innerHTML = highlight(rawText, lang);

      card.append(bar, body);
      pre.replaceWith(card);
      cleanups.push(() => {
        card.replaceWith(pre);
        delete code.dataset.cf;
      });
    }
  }

  // Inline `<code>` chips in prose — syntax-highlight the code-ish ones (count > 0,
  // print(x), True, user["k"]…) so code is coloured everywhere, not only in blocks.
  // Plain-word labels and bare identifiers are left untouched (looksLikeCode guard).
  const inlineCodes = Array.from(root.querySelectorAll<HTMLElement>('code'));
  for (const code of inlineCodes) {
    if (code.dataset.cf === '1') continue;
    if (code.closest('pre')) continue; // block code — handled above
    if (code.closest('.code-along') || code.closest('.py-run') || code.closest('.po-build') || code.closest('.rflow') || code.closest('.pforge')) continue; // owned by other engines
    if (code.children.length > 0) {
      // already carries markup (nested span/strong/glossary) — don't clobber it
      code.dataset.cf = '1';
      continue;
    }
    const raw = code.textContent ?? '';
    if (!raw.trim()) {
      code.dataset.cf = '1';
      continue;
    }
    const lang = detectLang(code, null, raw) ?? 'python';
    if (!looksLikeCode(raw, lang)) {
      code.dataset.cf = '1';
      continue;
    }
    code.dataset.cf = '1';
    const original = code.innerHTML;
    code.innerHTML = highlight(raw, lang);
    cleanups.push(() => {
      code.innerHTML = original;
      delete code.dataset.cf;
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
